import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { formatBranchMode } from '../../config/branchModes';
import UserStatusBadge from '../../components/common/UserStatusBadge';
import ListRecordIdBadge from '../../components/common/ListRecordIdBadge';
import ListPageLayout, {
    ListPageHeader,
    ListFilterTabs,
    ListSearchCard,
    ListLoadingSpinner,
    ListEmptyState,
    ListTableCard,
} from '../../components/layout/ListPageLayout';
import PaginationFooter from '../../components/common/PaginationFooter';
import SortableTableHeader from '../../components/common/SortableTableHeader';
import useSortedClientPagination from '../../hooks/useSortedClientPagination';
import { branchListSortColumns } from '../../config/tableSortColumns';

function useDebounced(value, delay = 200) {
    const [v, setV] = useState(value);
    useEffect(() => { const t = setTimeout(() => setV(value), delay); return () => clearTimeout(t); }, [value, delay]);
    return v;
}

const ManagerBranches = () => {
    const { t } = useTranslation();
    const [branches, setBranches] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [filter, setFilter] = useState('active');
    const [q, setQ] = useState('');
    const dq = useDebounced(q, 200);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setError('');
        API.get('/manager/branches/mine')
            .then(res => {
                if (!mounted) return;
                setBranches(Array.isArray(res.data) ? res.data : []);
            })
            .catch(err => {
                if (!mounted) return;
                console.error('Error fetching manager branches:', err);
                const msg = err?.response?.status === 403
                    ? t('pages.managerBranches.unauthorized')
                    : t('pages.managerBranches.loadFailed');
                setError(msg);
                setBranches([]);
            })
            .finally(() => mounted && setLoading(false));

        return () => { mounted = false; };
    }, []);

    const filtered = useMemo(() => {
        let result = branches || [];
        if (filter === 'active') {
            result = result.filter(b => b.active !== false);
        } else if (filter === 'archived') {
            result = result.filter(b => b.active === false);
        }
        const term = (dq || '').trim().toLowerCase();
        if (term) {
            const pick = (x) => String(x ?? '').toLowerCase();
            result = result.filter((b) => {
                const hay = [
                    b.name,
                    b.branchCode,
                    b.phoneNumber,
                    b.mode,
                    b.id,
                    b.branchId,
                ].map(pick).join(' ');
                return term.split(/\s+/).every(t => hay.includes(t));
            });
        }
        return result;
    }, [branches, dq, filter]);

    const pagination = useSortedClientPagination(filtered, {
        columns: branchListSortColumns,
        resetKey: `${filter}-${dq}`,
    });

    const tabs = [
        { key: 'all', label: t('common.all'), count: branches.length },
        { key: 'active', label: t('common.active'), count: branches.filter(b => b.active !== false).length },
        { key: 'archived', label: t('common.archived'), count: branches.filter(b => b.active === false).length },
    ];

    const total = filter === 'all'
        ? branches.length
        : filter === 'active'
            ? branches.filter(b => b.active !== false).length
            : branches.filter(b => b.active === false).length;

    return (
        <ListPageLayout>
            <ListPageHeader
                title={t('pages.managerBranches.title')}
                subtitle={t('pages.managerBranches.subtitle')}
            />

            <ListFilterTabs tabs={tabs} active={filter} onChange={setFilter} />

            <ListSearchCard
                value={q}
                onChange={setQ}
                placeholder={t('pages.managerBranches.searchPlaceholder')}
                shown={filtered.length}
                total={total}
                entityLabel={t('pages.managerBranches.entityLabel')}
            />

            {loading ? (
                <ListLoadingSpinner />
            ) : error ? (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>
            ) : filtered.length === 0 ? (
                <ListEmptyState
                    message={q ? t('pages.managerBranches.noMatch') : t('pages.managerBranches.empty')}
                    hint={q ? t('common.emptySearchHint') : t('pages.managerBranches.emptyHint')}
                />
            ) : (
                <ListTableCard>
                    <div className="overflow-x-auto">
                        <table className="min-w-full">
                            <thead className="table-header">
                                <tr>
                                    <SortableTableHeader label={t('common.idColumn')} columnKey="id" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.branchName')} columnKey="name" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.code')} columnKey="code" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.phone')} columnKey="phone" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('table.mode')} columnKey="mode" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                    <SortableTableHeader label={t('common.status')} columnKey="status" sortKey={pagination.sortKey} sortDir={pagination.sortDir} onSort={pagination.toggleSort} />
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {pagination.pageItems.map((b) => (
                                    <tr key={b.id || b.branchId} className="table-row">
                                        <td className="table-cell whitespace-nowrap">
                                            <ListRecordIdBadge id={b.id ?? b.branchId} />
                                        </td>
                                        <td className="table-cell">
                                            <div className="text-sm font-medium text-gray-900">
                                                {b.name || <span className="text-gray-400">—</span>}
                                            </div>
                                        </td>
                                        <td className="table-cell">{b.branchCode || <span className="text-gray-400">—</span>}</td>
                                        <td className="table-cell">{b.phoneNumber || <span className="text-gray-400">—</span>}</td>
                                        <td className="table-cell">
                                            <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
                                                {formatBranchMode(b.mode)}
                                            </span>
                                        </td>
                                        <td className="table-cell">
                                            <UserStatusBadge active={b.active} />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    {pagination.total > 0 && (
                        <PaginationFooter
                            page={pagination.page}
                            size={pagination.size}
                            total={pagination.total}
                            onPageChange={pagination.setPage}
                            onSizeChange={pagination.setSize}
                        />
                    )}
                </ListTableCard>
            )}
        </ListPageLayout>
    );
};

export default ManagerBranches;
